import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  ModalOverlay,
  SlidePanel,
  ModalContent,
  NotificationCard,
  NotificationList,
  NotificationListContainer,
  EmptyState,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription,
  FilterBar,
  ModalHeader,
  CloseButton,
  TabsContainer,
  Tab,
  TabBadge,
  CategoryPill,
  DateGroup,
  DateHeader,
  NotificationHeader,
  NotificationIconWrapper,
  NotificationBody,
  NotificationTitle,
  NotificationDescription,
  NotificationMeta,
  ClientInfo,
  TimeInfo,
  CategoryBadge,
  NotificationActions,
  ActionButton,
  LoadingCard,
  LoadingLine,
  ErrorMessage,
} from './styles';

import {
  BellIcon,
  CloseIcon,
  CheckIcon,
  CheckCircleIcon,
  TrashIcon,
  ExternalLinkIcon,
  ClockIcon,
  UserIcon,
  InboxIcon,
  AlertCircleIcon,
  getCategoryIcon,
} from './icons';

import { NOTIFICATION_CATEGORIES } from './constants';
import { NotificationService, Notification, NotificationFilters, NotificationCounts } from '../../services/notificationService';
import { useNotificationModal } from '@/src/context/notificationModal';

export function NotificationModal() {
  const { isOpen, closeModal } = useNotificationModal();
  const [groupedNotifications, setGroupedNotifications] = useState<Record<string, Notification[]>>({});
  const [counts, setCounts] = useState<NotificationCounts | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'pending' | 'completed'>('pending');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [pendingActions, setPendingActions] = useState<Set<number>>(new Set());

  const filters: NotificationFilters = useMemo(() => ({
    completed: activeTab === 'completed',
    category: selectedCategory,
  }), [activeTab, selectedCategory]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const [grouped, countsData] = await Promise.all([
        NotificationService.getGroupedNotifications(filters),
        NotificationService.getCounts(),
      ]);

      setGroupedNotifications(grouped);
      setCounts(countsData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar notificações');
      console.error('Erro ao carregar notificações:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen, loadData]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        closeModal();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, closeModal]);

  const updateNotificationOptimistically = useCallback((
    id: number,
    updates: Partial<Notification>
  ) => {
    setGroupedNotifications(prev => {
      const newGrouped = { ...prev };
      for (const date of Object.keys(newGrouped)) {
        newGrouped[date] = newGrouped[date].map(notification =>
          notification.id === id ? { ...notification, ...updates } : notification
        );
      }
      return newGrouped;
    });
  }, []);

  const removeNotificationOptimistically = useCallback((id: number) => {
    setGroupedNotifications(prev => {
      const newGrouped: Record<string, Notification[]> = {};
      for (const [date, notifications] of Object.entries(prev)) {
        const filtered = notifications.filter(n => n.id !== id);
        if (filtered.length > 0) {
          newGrouped[date] = filtered;
        }
      }
      return newGrouped;
    });

    setCounts(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        not_completed: Math.max(0, prev.not_completed - 1),
      };
    });
  }, []);

  const handleMarkAsCompleted = useCallback(async (id: number) => {
    if (pendingActions.has(id)) return;

    setPendingActions(prev => new Set(prev).add(id));
    updateNotificationOptimistically(id, { is_completed: true });

    try {
      await NotificationService.markAsCompleted(id);
      await loadData();
    } catch (err: any) {
      updateNotificationOptimistically(id, { is_completed: false });
      setError(err.response?.data?.message || 'Erro ao marcar notificação como concluída');
    } finally {
      setPendingActions(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  }, [loadData, pendingActions, updateNotificationOptimistically]);

  const handleMarkAsNotCompleted = useCallback(async (id: number) => {
    if (pendingActions.has(id)) return;

    setPendingActions(prev => new Set(prev).add(id));
    updateNotificationOptimistically(id, { is_completed: false });

    try {
      await NotificationService.markAsNotCompleted(id);
      await loadData();
    } catch (err: any) {
      updateNotificationOptimistically(id, { is_completed: true });
      setError(err.response?.data?.message || 'Erro ao marcar notificação como não concluída');
    } finally {
      setPendingActions(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  }, [loadData, pendingActions, updateNotificationOptimistically]);

  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const handleDeleteClick = useCallback((id: number) => {
    setDeleteConfirmId(id);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteConfirmId || pendingActions.has(deleteConfirmId)) return;

    const id = deleteConfirmId;
    setDeleteConfirmId(null);
    setPendingActions(prev => new Set(prev).add(id));
    removeNotificationOptimistically(id);

    try {
      await NotificationService.deleteNotification(id);
      await loadData();
    } catch (err: any) {
      await loadData();
      setError(err.response?.data?.message || 'Erro ao excluir notificação');
    } finally {
      setPendingActions(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  }, [deleteConfirmId, loadData, pendingActions, removeNotificationOptimistically]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmId(null);
  }, []);

  const isSafeRedirectUrl = useCallback((url: string): boolean => {
    try {
      const urlObj = new URL(url, window.location.origin);
      return ['http:', 'https:'].includes(urlObj.protocol);
    } catch {
      return false;
    }
  }, []);

  const normalizeToAdminUrl = useCallback((url: string): string => {
    try {
      const urlObj = new URL(url, window.location.origin);
      const toCurrentOrigin = (pathname: string) =>
        `${window.location.origin}${pathname}${urlObj.search}${urlObj.hash}`;

      const supportReplyMatch = urlObj.pathname.match(/^\/vendedor\/suporte\/(\d+)(?:\/)?$/);
      if (supportReplyMatch) {
        return toCurrentOrigin(`/support/reply/${supportReplyMatch[1]}`);
      }

      const taskPathMatch = urlObj.pathname.match(/^\/vendedor\/(?:tarefas|tasks)(?:\/.*)?$/i);
      if (taskPathMatch) {
        return toCurrentOrigin('/tasks');
      }

      // Fallback: remove prefixo legado de vendedor quando houver rota equivalente no SPA
      if (urlObj.pathname.startsWith('/vendedor')) {
        const normalizedPath = urlObj.pathname.replace(/^\/vendedor(?=\/|$)/, '') || '/';
        return toCurrentOrigin(normalizedPath);
      }

      return urlObj.toString();
    } catch {
      return url;
    }
  }, []);

  const handleRedirect = useCallback((notification: Notification) => {
    if (notification.redirect_url && isSafeRedirectUrl(notification.redirect_url)) {
      const targetUrl = normalizeToAdminUrl(notification.redirect_url);
      closeModal();
      window.location.assign(targetUrl);
    }
  }, [closeModal, isSafeRedirectUrl, normalizeToAdminUrl]);

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Agora';
    if (diffMins < 60) return `${diffMins}min atrás`;
    if (diffHours < 24) return `${diffHours}h atrás`;
    if (diffDays < 7) return `${diffDays}d atrás`;

    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
    });
  };

  const hasNotifications = Object.keys(groupedNotifications).length > 0;

  const renderNotification = (notification: Notification) => {
    const isPending = pendingActions.has(notification.id);
    const isDeleting = deleteConfirmId === notification.id;

    return (
      <NotificationCard
        key={notification.id}
        completed={notification.is_completed}
        category={notification.category}
        style={{ opacity: isPending ? 0.6 : undefined }}
        role="article"
        aria-label={`Notificação: ${notification.title}`}
      >
        <NotificationHeader>
          <NotificationIconWrapper category={notification.category || 'system'}>
            {getCategoryIcon(notification.category || 'system')}
          </NotificationIconWrapper>

          <NotificationBody>
            <NotificationTitle completed={notification.is_completed}>
              {notification.title}
            </NotificationTitle>
            <NotificationDescription>
              {notification.processed_description || notification.description}
            </NotificationDescription>

            <NotificationMeta>
              {notification.client && (
                <ClientInfo>
                  <UserIcon />
                  {notification.client.company_name || notification.client.name}
                </ClientInfo>
              )}
              <TimeInfo>
                <ClockIcon />
                {formatRelativeTime(notification.created_at)}
              </TimeInfo>
              {notification.category && (
                <CategoryBadge category={notification.category}>
                  {notification.category}
                </CategoryBadge>
              )}
            </NotificationMeta>
          </NotificationBody>

          <NotificationActions>
            {isDeleting ? (
              <>
                <ActionButton
                  variant="check"
                  onClick={handleDeleteConfirm}
                  title="Confirmar exclusão"
                  aria-label="Confirmar exclusão"
                >
                  <CheckIcon />
                </ActionButton>
                <ActionButton
                  variant="view"
                  onClick={handleDeleteCancel}
                  title="Cancelar"
                  aria-label="Cancelar exclusão"
                >
                  <CloseIcon />
                </ActionButton>
              </>
            ) : (
              <>
                {notification.redirect_url && (
                  <ActionButton
                    variant="view"
                    onClick={() => handleRedirect(notification)}
                    title="Ver detalhes"
                    aria-label="Ver detalhes"
                    disabled={isPending}
                  >
                    <ExternalLinkIcon />
                  </ActionButton>
                )}
                <ActionButton
                  variant="check"
                  onClick={() => notification.is_completed
                    ? handleMarkAsNotCompleted(notification.id)
                    : handleMarkAsCompleted(notification.id)
                  }
                  title={notification.is_completed ? 'Marcar como pendente' : 'Marcar como concluída'}
                  aria-label={notification.is_completed ? 'Marcar como pendente' : 'Marcar como concluída'}
                  disabled={isPending}
                >
                  {notification.is_completed ? <CheckCircleIcon /> : <CheckIcon />}
                </ActionButton>
                <ActionButton
                  variant="delete"
                  onClick={() => handleDeleteClick(notification.id)}
                  title="Excluir"
                  aria-label="Excluir notificação"
                  disabled={isPending}
                >
                  <TrashIcon />
                </ActionButton>
              </>
            )}
          </NotificationActions>
        </NotificationHeader>
      </NotificationCard>
    );
  };

  const renderLoadingSkeleton = () => (
    <>
      {[1, 2, 3].map((i) => (
        <LoadingCard key={i}>
          <LoadingLine width="60%" />
          <LoadingLine width="90%" />
          <LoadingLine width="40%" />
        </LoadingCard>
      ))}
    </>
  );

  const modalContent = (
    <>
      <ModalOverlay isOpen={isOpen} onClick={closeModal} aria-hidden="true" />
      <SlidePanel
        isOpen={isOpen}
        role="dialog"
        aria-modal="true"
        aria-labelledby="notification-modal-title"
      >
        <ModalHeader>
          <h2 id="notification-modal-title">
            <BellIcon />
            Notificações
          </h2>
          <CloseButton onClick={closeModal} aria-label="Fechar notificações">
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        <TabsContainer role="tablist">
          <Tab
            active={activeTab === 'pending'}
            onClick={() => setActiveTab('pending')}
            role="tab"
            aria-selected={activeTab === 'pending'}
            aria-controls="pending-notifications"
          >
            Pendentes
            {counts && counts.not_completed > 0 && (
              <TabBadge>{counts.not_completed > 99 ? '99+' : counts.not_completed}</TabBadge>
            )}
          </Tab>
          <Tab
            active={activeTab === 'completed'}
            onClick={() => setActiveTab('completed')}
            role="tab"
            aria-selected={activeTab === 'completed'}
            aria-controls="completed-notifications"
          >
            Concluídas
            {counts && counts.completed > 0 && (
              <TabBadge variant="secondary">{counts.completed > 99 ? '99+' : counts.completed}</TabBadge>
            )}
          </Tab>
        </TabsContainer>

        <ModalContent>
          <FilterBar role="group" aria-label="Filtrar por categoria">
            {NOTIFICATION_CATEGORIES.map((cat) => (
              <CategoryPill
                key={cat.value}
                active={selectedCategory === cat.value}
                color={cat.color}
                onClick={() => setSelectedCategory(cat.value)}
                aria-pressed={selectedCategory === cat.value}
              >
                {cat.label}
              </CategoryPill>
            ))}
          </FilterBar>

          {error && (
            <ErrorMessage role="alert">
              <AlertCircleIcon />
              {error}
            </ErrorMessage>
          )}

          <NotificationListContainer
            id={activeTab === 'pending' ? 'pending-notifications' : 'completed-notifications'}
            role="tabpanel"
          >
            {loading ? (
              renderLoadingSkeleton()
            ) : hasNotifications ? (
              Object.entries(groupedNotifications).map(([date, items]) => (
                <DateGroup key={date}>
                  <DateHeader>{date}</DateHeader>
                  <NotificationList role="list">
                    {items.map(renderNotification)}
                  </NotificationList>
                </DateGroup>
              ))
            ) : (
              <EmptyState>
                <EmptyIcon>
                  <InboxIcon />
                </EmptyIcon>
                <EmptyTitle>
                  {activeTab === 'pending'
                    ? 'Tudo em dia!'
                    : 'Nenhuma notificação concluída'}
                </EmptyTitle>
                <EmptyDescription>
                  {activeTab === 'pending'
                    ? 'Você não tem notificações pendentes no momento.'
                    : 'Notificações concluídas aparecerão aqui.'}
                </EmptyDescription>
              </EmptyState>
            )}
          </NotificationListContainer>
        </ModalContent>
      </SlidePanel>
    </>
  );

  return createPortal(modalContent, document.body);
}
